import { Injectable } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  ReplaySubject,
  startWith,
} from 'rxjs';
import { filter } from 'rxjs/operators';
import { isFullScreeningAppInCall } from '../sessions/session/services/full-screen-app-manager.service';
import {
  ISessionViewState,
  SessionSharedDataService,
  SessionView,
} from './session-shared-data.service';
import { SessionsVptService } from './sessions-vpt.service';
import { ISpaceLeaderModeUI, SpaceLeaderModeService } from './space-leader-mode.service';
import { FLAGS, FlagsService } from './flags.service';

@Injectable({
  providedIn: 'root',
})
export class SpaceZoomCutoffService {
  /**
   * based on the Rules we define for cut off ( destroy ) it will emit boolean value
   * we define rules now based on the zooming level on different window sizes ( with )
   */
  public shouldCutOff$: Observable<boolean>;
  private _cutOffThreshold = new ReplaySubject<number>();
  public cutOffThreshold$ = this._cutOffThreshold.asObservable();
  private _zoomLevel = new ReplaySubject<number>();
  public zoomLevel$ = this._zoomLevel.asObservable();
  private _shouldCutOff = false;

  constructor(
    private sharedDataService: SessionSharedDataService,
    private sessionsVptService: SessionsVptService,
    private spaceLeaderModeService: SpaceLeaderModeService,
    private flagsService: FlagsService,
  ) {
    this.shouldCutOff$ = combineLatest([
      this.sessionsVptService.viewportTransform.pipe(
        map(() => this.zoomLevel),
        distinctUntilChanged(),
      ),
      this.spaceLeaderModeService.activeSpaceLeaderModeState$,
      this.sharedDataService.sessionView.current$,
      this.flagsService.featureFlagChanged(FLAGS.ZOOMED_OUT_APPS_OVERLAY),
    ]).pipe(
      filter(() => this.flagsService.isFlagEnabled(FLAGS.ZOOMED_OUT_APPS_OVERLAY)),
      map(([currentZoom, leaderModeState, sessionView]) => {
        const zoomCutoffThreshold = this.calculateZoomCutoffThreshold(
          window.innerWidth,
          leaderModeState,
        );

        this._cutOffThreshold.next(zoomCutoffThreshold);
        this._zoomLevel.next(this.zoomLevel);

        const skipCutOff = this.shouldSkipCutOff(sessionView, leaderModeState);
        // cut off should happen on zooming level and also on not full screen app
        this._shouldCutOff =
          zoomCutoffThreshold >= currentZoom &&
          sessionView.view !== SessionView.FULLSCREEN_APP &&
          !skipCutOff;
        return this._shouldCutOff;
      }),
      startWith(false),
      distinctUntilChanged(),
    );
  }

  // if fullscreen app in call, keep it rendered for the tile host
  // if leader mode is on, skip cut off
  private shouldSkipCutOff(
    sessionView: ISessionViewState,
    leaderModeState: ISpaceLeaderModeUI,
  ): boolean {
    return (
      (sessionView.view === SessionView.EXPANDED && isFullScreeningAppInCall(sessionView)) ||
      this.skipCutOffWhenLeaderModeEnabled(leaderModeState)
    );
  }

  private skipCutOffWhenLeaderModeEnabled(leaderModeState: ISpaceLeaderModeUI): boolean {
    return (
      this.flagsService.isFlagEnabled(FLAGS.SKIP_APPS_OVERLAY_WHEN_LEADER_MODE_ENABLED) &&
      leaderModeState.leaderModeEnabled
    );
  }

  get zoomLevel(): number {
    return this.sharedDataService.fabricCanvas?.getZoom() || 1;
  }

  public get shouldCutOff() {
    return this._shouldCutOff;
  }

  /**
   * this method will calculate the minimum zoom level we need to cut off and destroy the application
   * it checks the viewport width and based on some conditions we return the minimum value
   * Also the threshold can be overridden to a 0.25 if leader mode is one
   * @param viewportWidth
   * @param leaderModeState
   */
  calculateZoomCutoffThreshold(viewportWidth: number, leaderModeState: ISpaceLeaderModeUI): number {
    let zoomCutoffThreshold;
    // if width of the viewport is more than 1600, minimum zoom is 25%
    if (viewportWidth > 1600) {
      zoomCutoffThreshold = 0.25;
      //   if width of the viewport is more than 400, minimum zoom is 40%
    } else if (viewportWidth < 400) {
      zoomCutoffThreshold = 0.4;
      //  for anything else we calculate the zoom based on the method below
    } else {
      zoomCutoffThreshold = 0.4 + ((0.25 - 0.4) / (1600 - 400)) * (viewportWidth - 400);
    }

    // if leader mode is on, zoom cutoff should be 25% for all
    if (leaderModeState.leaderModeEnabled) {
      zoomCutoffThreshold = 0.25;
    }

    return zoomCutoffThreshold;
  }
}
